import React, { useState, useEffect, useContext, useCallback } from "react";
import { Button, Table, Space, notification, Input, Select, Tag } from "antd";
import {
  SearchOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import TemplatePointsForm from "../components/Forms/TemplatePointsForm";
import axios from "axios";
import "moment-timezone";
import GlobalContext from "../utils/GlobalContext";
import { v4 as uuidv4 } from "uuid";
import MessageDialog from "../components/MessageDialog";
import LoadingModal from "../utils/LoadingModal";
// import formatTimestamp from "../utils/FormatTimestamp";

const PAGE_SIZE = 15;
const { Option } = Select;

const AddTemplatePointsTable = ({currentTemplateId, meeting_type_id, department_id}) => {
  const globals = useContext(GlobalContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTemplatePointsForm, setShowTemplatePointsForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentTemplatePoint, setCurrentTemplatePoint] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
  });
  const [messageVisible, setMessageVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [meeting_types, setMeetingTypes] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [meetingTypesId, setMeetingTypesId] = useState(null);
  const [departmentsId, setDepartmentsId] = useState(null);

  const columns = [
    // {
    //   title: "ID",
    //   dataIndex: "id",
    //   key: "id",
    //   width: 40,
    // },
    {
      title: "Nombre del punto",
      dataIndex: "name",
      key: "name",
      // width: 180,
      render: (_, record) => {
        if (record.departmentsId === null && record.meetingTypesId === null) {
            return (
            <span>{record.name} <Tag color="blue">todos</Tag></span>
            )
        }
        else{
          return (
            <span>{record.name}</span>
          )
        }
      },
    },
    {
      title: "Departamento",
      dataIndex: ["department", "name"],
      key: "department",
    },
    {
      title: "Tipo de reunión",
      dataIndex: ["meeting_type", "name"],
      key: "meeting_type",
    },
    {
      title: "Tipo de punto",
      dataIndex: ["point_type", "name"],
      key: "point_type",
    },
    {
      title: "Clasificación",
      dataIndex: ["classification", "name"],
      key: "classification",
    },
    // {
    //   title: "Descripción",
    //   dataIndex: "description",
    //   key: "description",
    // },
    {
      title: "🕑",
      dataIndex: "duration",
      key: "duration",
      // width: 50,
      render: (text) => {
        return (text) ? `${text} min`: `0 min`;
      },
    },
    {
      title: "Agregar",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Space size="middle">
          <Button
            icon={<PlusCircleOutlined />}
            onClick={() => handleActionClick(record.id)}
            shape="circle"
          />
        </Space>
      ),
    },
  ];

  const fetchMeetingTypes = useCallback(async () => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 100));
      const response = await axios.get(
        `${globals.SERVICE_API_DB}/v1/list-meeting-types`,
        {
          headers: {
            "x-token": globals.token,
          },
        }
      );
      setMeetingTypes(response?.data?.data);
      setMeetingTypesId(meeting_type_id);
    } catch (error) {
      console.error("Error fetching meeting types", error.response);
      notification.error({
        message: "Error obteniendo tipos de reunión",
        placement: "bottomRight",
      });
    }
    // setLoadingClients(false);
  }, [globals.SERVICE_API_DB, globals.token, meeting_type_id]);

  const fetchDepartments = useCallback(async () => {
    try {
      // setLoadingClients(true);
      await new Promise((resolve) => setTimeout(resolve, 100));
      const response = await axios.get(
        `${globals.SERVICE_API_DB}/v1/list-departments`,
        {
          headers: {
            "x-token": globals.token,
          },
        }
      );
      setDepartments(response?.data?.data);
      setDepartmentsId(department_id);
    } catch (error) {
      console.error("Error fetching departments:", error.response);
      notification.error({
        message: "Error obteniendo departamentos",
        placement: "bottomRight",
      });
    }
    // setLoadingClients(false);
  }, [globals.SERVICE_API_DB, globals.token, department_id]);

  const fetchData = useCallback(
    async (newPagination, search, meetingTypesId, departmentsId) => {
      let page = 1;
      let pageSize = PAGE_SIZE;
      if (newPagination) {
        page = newPagination.current;
        pageSize = newPagination.pageSize;
      }
      try {
        setLoading(true);
        await new Promise((resolve) => setTimeout(resolve, 100));
        const response = await axios.get(
          `${globals.SERVICE_API_DB}/v1/list-template-point`,
          {
            headers: {
              "x-token": globals.token,
            },
            params: {
              page,
              limit: pageSize,
              search,
              meetingTypesId,
              departmentsId,
              onlyActive: true,	
            },
          }
        );
        setData(response?.data?.data.rows);
        if (newPagination) {
          setPagination({
            ...newPagination,
            total: response?.data?.data.count,
          });
        } else {
          setPagination({
            ...pagination,
            total: response?.data?.data.count,
          });
        }
        notification.success({
          message: "Información obtenida exitosamente!",
          placement: "bottomRight",
          duration: 2,
        });
      } catch (error) {
        console.error("Error fetching data:", error.response);
        if (error?.response?.data.status === -2){
          notification.warning({
            message: "No hay información disponible!",
            placement: "bottomRight",
          });
          setData([]);
        }else{
          notification.error({
            message: "Error obteniendo información!",
            placement: "bottomRight",
          });
        }
      }
      setLoading(false);
    },
    // eslint-disable-next-line
    [globals.SERVICE_SERVICE_API_DB]
  );

  useEffect(() => {
    fetchMeetingTypes();
    fetchDepartments();
    fetchData(null, null, meeting_type_id, department_id);
  }, [fetchData, fetchMeetingTypes, fetchDepartments, meeting_type_id, department_id]);

  const handleActionClick = async (id) => {
    try {
      const result = await axios.post(
        `${globals.SERVICE_API_DB}/v1/create-point-details/${currentTemplateId}`,
        {
          "templateId": currentTemplateId,
          "template_points_ids": [id]
        },
        {
          headers: {
            "x-token": globals.token,
          },
        }
      );
      //ESTO VALIDA EL [0] PORQUE EL ENDPOINT ES CAPAZ
      //DE RECIBIR MULTIPLES ID'S DE PUNTOS DE PLANTILLA
      //PERO ACTUALMENTE SOLO SE HACE 1 A LA VEZ.
      if (result?.data?.data[0].status === -3) {
        notification.warning({
          message: "Punto de plantilla ya existe en la plantilla",
          placement: "bottomRight",
          duration: 2,
        });
        return;
      }
      notification.success({
        message: "Punto de plantilla agregado!",
        placement: "bottomRight",
        duration: 1,
      });
      //delete from table
      setData(data.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Error fetching template point:", error.response);
      notification.error({
        message: "Error obteniendo punto de plantilla",
        placement: "bottomRight",
        duration: 2,
      });
    }
  };

  async function handleTableChange(newPagination) {
    fetchData(newPagination, searchText, meetingTypesId, departmentsId);
  }

  const saveTemplatePointForm = async ({ status, newTemplatePoint }) => {
    setShowTemplatePointsForm(false);
    if (isEdit) {
      setIsEdit(false);
      notification.success({
        message: "Punto de plantilla editado exitosamente!",
        placement: "bottomRight",
        duration: 2,
      });
      return;
    }
    if (status === "created") {
      setData([newTemplatePoint, ...data]);
    }

    notification.success({
      message: "Punto de plantilla creado exitosamente!",
      placement: "bottomRight",
      duration: 2,
    });
  };

  const closeTemplatePointForm = () => {
    setShowTemplatePointsForm(false);
    setIsEdit(false);
    setCurrentTemplatePoint(null);
  };

  const deleteTemplate = async () => {
    setMessageVisible(false);
    try {
      await axios.delete(
        `${globals.SERVICE_API_DB}/v1/delete-template/${currentTemplatePoint.id}`,
        {
          headers: {
            "x-token": globals.token,
          },
        }
      );
      setData(data.filter((item) => item.id !== currentTemplatePoint.id));
      notification.success({
        message: "Punto de plantilla eliminado exitosamente!",
        placement: "bottomRight",
        duration: 2,
      });
    } catch (err) {
      console.err(err.response.data);
      notification.error({
        message: "Error eliminando departamento",
        placement: "bottomRight",
      });
      return;
    }
  };

  const handleRefresh = () => {
    fetchData(null, searchText, meetingTypesId, departmentsId);
  };

  const handleSearch = async () => {
    fetchData(null, searchText, meetingTypesId, departmentsId);
  };

  const handleSearchEnterKey = async (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };
  const filterMeetingTypeOption = (input, option) =>
    (option?.children ?? "").toLowerCase().includes(input.toLowerCase());

  const filterDepartmentsOption = (input, option) =>
    (option?.children ?? "").toLowerCase().includes(input.toLowerCase());

  return (
    <div>
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}
      >
        <h2 style={{ marginRight: "16px" }}>Puntos de plantillas</h2>
        <div style={{ flexGrow: 1, borderBottom: "1px solid #ccc" }} />
        <Button
          onClick={() => {
            setIsEdit(false);
            setShowTemplatePointsForm(true);
          }}
          style={{ marginRight: "8px" }}
        >
          Crear
        </Button>
        <Button
          onClick={handleRefresh}
          disabled={loading}
          style={{ marginRight: "8px" }}
        >
          Refrescar
        </Button>
      </div>
      <Input
        allowClear
        placeholder="Ingresa un nombre"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 16, width: 200 }}
        prefix={<SearchOutlined />}
        onKeyDown={handleSearchEnterKey}
      />
      
      <Select
        allowClear
        style={{ width: 200, marginLeft: 8 }}
        showSearch
        value={departmentsId}
        onChange={(value) => setDepartmentsId(value)}
        placeholder="Departamentos"
        filterOption={filterDepartmentsOption}
        optionFilterProp="children"
      >
        {departments.map((option) => (
          <Option key={option.id} value={option.id}>
            {option.name}
          </Option>
        ))}
      </Select>
      <Select
        allowClear
        style={{ width: 200, marginLeft: 8 }}
        showSearch
        value={meetingTypesId}
        onChange={(value) => setMeetingTypesId(value)}
        placeholder="Tipo de reunión"
        filterOption={filterMeetingTypeOption}
        optionFilterProp="children"
      >
        {meeting_types.map((option) => (
          <Option key={option.id} value={option.id}>
            {option.name}
          </Option>
        ))}
      </Select>
      <Button onClick={handleSearch} style={{ width: 100, marginLeft: "8px" }}>
        Buscar
      </Button>
      <div
        className="site-layout-background h-auto"
        style={{
          padding: 10,
          display: "flex",
          flexDirection: "column",
          margin: 0,
          height: "auto",
          minHeight: "350px",
          overflow: "auto",
          backgroundColor: "#ffffff",
        }}
      >
        <Table
          rowKey={(r) => uuidv4()}
          columns={columns}
          dataSource={data}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ y: "max-content" }}
          size="small"
        />
      </div>
      <LoadingModal open={loading} />

      <TemplatePointsForm
        open={showTemplatePointsForm}
        onFinish={saveTemplatePointForm}
        onCancel={closeTemplatePointForm}
        isEdit={isEdit}
        currentTemplatePoint={currentTemplatePoint}
      />

      <MessageDialog
        visible={messageVisible}
        title="Está seguro de eliminar esta plantilla?"
        message="Esta acción no se puede deshacer."
        onOk={deleteTemplate}
        onCancel={() => setMessageVisible(false)}
        confirm={true}
      />
    </div>
  );
};

export default AddTemplatePointsTable;
